import React, { useRef } from 'react';
import { BadgesProps } from '@/shared/types/badges';
import { Tag } from '@/modules/Tag';
import { cn } from '@/shared/utils/cn';
import { useBadges } from '../hooks/useBadges';

// Mapeo de tamaños a clases CSS (reutilizando patrón de TagsMultiple)
const SIZE_CLASSES = {
  xs: 'ui-px-8 ui-py-4 ui-min-h-control-xs',
  sm: 'ui-px-8 ui-py-4 ui-min-h-control-sm',
  md: 'ui-p-8 ui-min-h-control-md',
  lg: 'ui-p-8 ui-min-h-control-lg',
} as const;

export const Badges: React.FC<BadgesProps> = ({
  value,
  placeholder = 'Escribe y presiona Enter...',
  label,
  helperText,
  error,
  defaultColor = 'accent',
  colorCycle,
  size = 'sm',
  disabled = false,
  fullWidth = true,
  maxBadges,
  onChange,
  onAdd,
  onRemove,
  onFocus,
  onBlur,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    badges,
    inputValue,
    isFocused,
    setInputValue,
    setIsFocused,
    addBadge,
    removeBadge,
    canAddMore,
  } = useBadges({
    initialValue: value ?? [],
    defaultColor,
    colorCycle,
    maxBadges,
    onChange,
    onAdd,
    onRemove,
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addBadge(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && badges.length > 0) {
      // Eliminar el último badge si el input está vacío
      const lastBadge = badges[badges.length - 1];
      removeBadge(lastBadge.id);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleContainerClick = () => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  };

  // Clases del contenedor principal (estilo input)
  const containerClasses = cn(
    'ui-flex ui-flex-wrap ui-items-center ui-gap-4',
    'ui-rounded-control-sm ui-border ui-bg-fill',
    'ui-transition-colors ui-duration-150',
    SIZE_CLASSES[size],
    disabled
      ? 'ui-border-disabled ui-bg-surface-disabled ui-cursor-not-allowed'
      : error
        ? 'ui-border-danger ui-bg-surface-danger'
        : isFocused
          ? 'ui-border-brand'
          : 'ui-border-default hover:ui-border-brand',
    !disabled && 'ui-cursor-text',
    className,
  );

  // Clases del label
  const labelClasses = cn(
    'ui-text-body-sm-regular ui-font-body-sm-bold',
    error ? 'ui-text-danger' : 'ui-text-primary',
  );

  return (
    <div className={cn('ui-flex ui-flex-col ui-gap-1', fullWidth ? 'ui-w-full' : 'ui-w-fit')}>
      {/* Label */}
      {label && (
        <div className="ui-flex ui-flex-row ui-items-center ui-gap-1 ui-leading-tight">
          <label className={labelClasses}>{label}</label>
        </div>
      )}

      {/* Contenedor principal */}
      <div ref={containerRef} className={containerClasses} onClick={handleContainerClick}>
        {/* Badges */}
        {badges.map((badge) => (
          <Tag
            key={badge.id}
            label={badge.label}
            color={disabled ? 'disabled' : badge.color}
            disabled={disabled}
            onClose={disabled ? undefined : () => removeBadge(badge.id)}
          />
        ))}

        {/* Input */}
        {canAddMore && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={badges.length === 0 ? placeholder : ''}
            disabled={disabled}
            className={cn(
              'ui-min-w-[120px] ui-flex-1 ui-bg-transparent ui-outline-none',
              'ui-text-body-sm-regular ui-text-primary',
              'placeholder:ui-text-tertiary',
              disabled && 'ui-cursor-not-allowed ui-text-disabled',
            )}
          />
        )}
      </div>

      {/* Helper text */}
      {helperText && !error && (
        <p className="ui-text-body-xs-regular ui-text-tertiary">{helperText}</p>
      )}

      {/* Error */}
      {error && <p className="ui-text-body-xs-regular ui-text-danger">{error}</p>}
    </div>
  );
};

Badges.displayName = 'Badges';
